import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';

import * as css from './VideoRatingPopup.css';
import {ManagementState, VideoRatingPopupProps} from './types';
import Close from '../../../component/close/Close';
import { getVideoRatingsData, resetVideoRatings } from '../../../redux/management/managementActions';
import MobilePopup from '../../../component/newPopup/mobilePopup/MobilePopup';
import Table from '../../../component/newTable/Table';
import VideoRatingRow from './VideoRatingRow';
import VideoRatingHeader from './VideoRatingHeader';

export type {VideoRatingData} from './types';

const LIMIT = 20;

function VideoRatingPopup({
    isOpen,
    showClass,
    onClose,
    onEditVideo,
}: VideoRatingPopupProps) {
    const {t} = useTranslation('layout');
    const {videos, videosXHR} = useSelector((state: {management: ManagementState}) => ({
        videos: state.management.videoRatings,
        videosXHR: state.management.videoRatingsXHR
    }));
    const [isEndOfResults, setIsEndOfResults] = useState(false);

    const fetchVideoRatings = useCallback(async (offset: number) => {
        const isEnd = await getVideoRatingsData(LIMIT, offset);
        setIsEndOfResults(isEnd);
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchVideoRatings(0);
        }
        return () => {
            resetVideoRatings();
        };
    }, []);

    const loadMore = useCallback(() => {
        if (isEndOfResults) return;
        fetchVideoRatings(videos.length);
    }, [isEndOfResults, videos.length, fetchVideoRatings]);

    const handleEdit = useCallback((videoId: number) => {
        onEditVideo?.(videoId, '');
    }, [onEditVideo]);

    const customHeader = (
        <css.popupHeader>
            <Close onClick={onClose} className="popupRating" />
            <css.headerTitle>{t('ratingSummaryTitle')}</css.headerTitle>
        </css.popupHeader>
    );

    if (!isOpen) return null;

    return (
        <MobilePopup 
            showClass={showClass} 
            close={onClose} 
            title={undefined}
            list={undefined} 
            forwardRef={undefined} 
            dataCyName="videoRatingPopup" 
            TopComponent={customHeader}
            height="100%"
            cssStyle={`width: 1025px`}
        >

                {/* Content */}
                <css.content>
                    {videos.length > 0 || videosXHR === 'LOADING' ? (
                        <Table
                            list={videos.map(video => ({
                                comp: VideoRatingRow,
                                props: {
                                    video,
                                    onEdit: handleEdit
                                },
                                key: video.id
                            }))}
                            header={VideoRatingHeader}
                            scrollProps={{
                                XHR: videosXHR,
                                onScrollEnd: loadMore,
                                toShowLoader: videosXHR === 'LOADING'
                            }}
                            checked={undefined}
                            buttonsChildren={undefined}
                            searchProps={undefined}
                            customScrollbar={{}}
                        />
                    ) : (
                        <css.emptyState>
                            {t('noResults')}
                        </css.emptyState>
                    )}
                </css.content>
        </MobilePopup>

    );
}

export default VideoRatingPopup;
